import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getPlaylist, getPlaylists, createPlaylist, updatePlaylist, getPlaylistTracks } from '../api';
import usePlayerStore from '../store/playerStore';

const GENRES = [
  'Alternative', 'Blues', 'Christmas', 'Country', 'Dance Pop',
  'EDM', 'Electronic/downtempo', 'Electronica/Dance', 'Folk',
  'Hip-Hop/Rap', 'Hip Hop/Rap-Hardcore', 'Indie', 'Jazz', 'Metal',
  'Metal/Alt', 'Paramount Records', 'Pop', 'Pop Newer', 'Punk Pop',
  'R&B', 'Reggae', 'Rock', 'Rock Prog', 'Rock/Jam', 'Shoegaze',
  'Vocal', 'World',
].sort();

const FIELDS = [
  { value: 'genre__name',  label: 'Genre',       type: 'genre' },
  { value: 'year',         label: 'Year',        type: 'year' },
  { value: 'play_count',   label: 'Play Count',  type: 'number' },
  { value: 'skip_count',   label: 'Skip Count',  type: 'number' },
  { value: 'bpm',          label: 'BPM',         type: 'number' },
  { value: 'is_loved',     label: 'Favorited',   type: 'boolean' },
  { value: 'date_added',   label: 'Date Added',  type: 'date' },
  { value: 'last_played',  label: 'Last Played', type: 'date' },
];

const ADDITIVE_FIELDS = [
  { value: 'title',        label: 'Title',       type: 'text' },
  { value: 'artist__name', label: 'Artist',      type: 'text' },
  { value: 'album__title', label: 'Album',       type: 'text' },
  { value: 'playlist',     label: 'Playlist',    type: 'playlist' },
];

const OPS_BY_TYPE = {
  text: [
    { value: 'contains',     label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'equals',       label: 'is exactly' },
    { value: 'not_equals',   label: 'is not' },
  ],
  number: [
    { value: 'equals',      label: 'is' },
    { value: 'not_equals',  label: 'is not' },
    { value: 'gt',          label: 'is greater than' },
    { value: 'gte',         label: 'is greater than or equal to' },
    { value: 'lt',          label: 'is less than' },
    { value: 'lte',         label: 'is less than or equal to' },
  ],
  boolean: [
    { value: 'is_true',  label: 'is true' },
    { value: 'is_false', label: 'is false' },
  ],
  date: [
    { value: 'gt',  label: 'is after' },
    { value: 'lt',  label: 'is before' },
    { value: 'gte', label: 'is on or after' },
    { value: 'lte', label: 'is on or before' },
  ],
  year: [
    { value: 'year_list', label: 'is any of' },
    { value: 'equals',    label: 'is exactly' },
    { value: 'gt',        label: 'is after' },
    { value: 'gte',       label: 'is after or equal to' },
    { value: 'lt',        label: 'is before' },
    { value: 'lte',       label: 'is before or equal to' },
  ],
  genre: [
    { value: 'genre_list',   label: 'is any of' },
    { value: 'equals',       label: 'is exactly' },
    { value: 'not_equals',   label: 'is not' },
    { value: 'contains',     label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
  ],
};

const ORDER_BY_OPTIONS = [
  { value: 'artist__name', label: 'Artist' },
  { value: 'album__title', label: 'Album' },
  { value: 'title',        label: 'Title' },
  { value: 'year',         label: 'Year (oldest first)' },
  { value: '-year',        label: 'Year (newest first)' },
  { value: '-play_count',  label: 'Most Played' },
  { value: 'play_count',   label: 'Least Played' },
  { value: '-date_added',  label: 'Recently Added' },
  { value: 'date_added',   label: 'Oldest Added' },
  { value: '-last_played', label: 'Recently Played' },
  { value: 'bpm',          label: 'BPM (slowest first)' },
  { value: '-bpm',         label: 'BPM (fastest first)' },
];

function newCondition() {
  return { id: Date.now(), field: 'genre__name', op: 'genre_list', value: '' };
}

function newAdditiveCondition() {
  return { id: Date.now(), field: 'artist__name', op: 'contains', value: '' };
}

function getFieldType(fieldValue) {
  return FIELDS.find(f => f.value === fieldValue)?.type || 'text';
}

function getAdditiveFieldType(fieldValue) {
  return ADDITIVE_FIELDS.find(f => f.value === fieldValue)?.type || 'text';
}

function GenrePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (genre) => {
    const next = selected.includes(genre) ? selected.filter(g => g !== genre) : [...selected, genre];
    onChange(next.join(', '));
  };

  const displayLabel = selected.length === 0 ? 'Select genres…'
    : selected.length === 1 ? selected[0]
    : `${selected.length} genres selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        ...styles.input, minWidth: 220, textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        color: selected.length ? 'var(--text)' : 'var(--text-muted)',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
        <span style={{ marginLeft: 8, opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div style={pickerStyles.dropdown}>
          <div style={pickerStyles.header}>
            <span>{selected.length} selected</span>
            {selected.length > 0 && <button style={pickerStyles.clearBtn} onClick={() => onChange('')}>Clear all</button>}
          </div>
          <div style={pickerStyles.list}>
            {GENRES.map(genre => (
              <label key={genre} style={pickerStyles.item}>
                <input type="checkbox" checked={selected.includes(genre)} onChange={() => toggle(genre)}
                  style={{ marginRight: 8, accentColor: 'var(--accent)' }} />
                {genre}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlaylistPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data } = useQuery(
    ['playlists-picker'],
    () => getPlaylists({ page_size: 500 }).then(r => r.data.results || []),
    { staleTime: 60000 }
  );
  const playlists = (data || []).filter(p => !p.is_smart);
  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    const sid = String(id);
    const next = selected.includes(sid) ? selected.filter(s => s !== sid) : [...selected, sid];
    onChange(next.join(','));
  };

  const selectedNames = playlists.filter(p => selected.includes(String(p.id))).map(p => p.name);
  const displayLabel = selected.length === 0 ? 'Select playlists…'
    : selected.length === 1 ? selectedNames[0]
    : `${selected.length} playlists selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        ...styles.input, minWidth: 260, textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        color: selected.length ? 'var(--text)' : 'var(--text-muted)',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
        <span style={{ marginLeft: 8, opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div style={pickerStyles.dropdown}>
          <div style={pickerStyles.header}>
            <span>{selected.length} selected</span>
            {selected.length > 0 && <button style={pickerStyles.clearBtn} onClick={() => onChange('')}>Clear all</button>}
          </div>
          <div style={pickerStyles.list}>
            {playlists.map(p => (
              <label key={p.id} style={pickerStyles.item}>
                <input type="checkbox" checked={selected.includes(String(p.id))} onChange={() => toggle(p.id)}
                  style={{ marginRight: 8, accentColor: 'var(--accent)' }} />
                {p.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const pickerStyles = {
  dropdown: {
    position: 'absolute', left: 0, top: '110%', zIndex: 100,
    background: 'var(--bg2)', border: '1px solid var(--border)',
    borderRadius: 10, minWidth: 240,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)', overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 14px', borderBottom: '1px solid var(--border)',
    fontSize: 11, color: 'var(--text-muted)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  clearBtn: { background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 11, padding: 0 },
  list: { maxHeight: 280, overflowY: 'auto', padding: '6px 0' },
  item: { display: 'flex', alignItems: 'center', padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--text)', userSelect: 'none' },
};

function ConditionRow({ condition, onChange, onRemove, canRemove }) {
  const fieldType = getFieldType(condition.field);
  const ops = OPS_BY_TYPE[fieldType] || OPS_BY_TYPE.text;
  const isBoolean = fieldType === 'boolean';
  const isYearList = condition.op === 'year_list';
  const isGenreList = condition.op === 'genre_list';

  const handleFieldChange = (field) => {
    const newType = getFieldType(field);
    const newOps = OPS_BY_TYPE[newType];
    onChange({ ...condition, field, op: newOps[0].value, value: '' });
  };

  const getPlaceholder = () => {
    if (isYearList) return 'e.g. 1971, 1982-1986, 1999';
    if (fieldType === 'number') return '0';
    if (fieldType === 'date') return '';
    return 'value…';
  };

  return (
    <div style={styles.conditionRow}>
      <select style={styles.select} value={condition.field} onChange={e => handleFieldChange(e.target.value)}>
        {FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>
      <select style={styles.select} value={condition.op} onChange={e => onChange({ ...condition, op: e.target.value, value: '' })}>
        {ops.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
      </select>
      {!isBoolean && (
        isGenreList ? (
          <GenrePicker value={condition.value} onChange={val => onChange({ ...condition, value: val })} />
        ) : (
          <input
            style={{ ...styles.input, ...(isYearList ? { minWidth: 240 } : {}) }}
            type={fieldType === 'date' ? 'date' : (fieldType === 'number' && !isYearList) ? 'number' : 'text'}
            value={condition.value}
            placeholder={getPlaceholder()}
            onChange={e => onChange({ ...condition, value: e.target.value })}
          />
        )
      )}
      {canRemove && <button style={styles.removeBtn} onClick={onRemove} title="Remove condition">✕</button>}
    </div>
  );
}

function AdditiveConditionRow({ condition, onChange, onRemove }) {
  const fieldType = getAdditiveFieldType(condition.field);
  const ops = OPS_BY_TYPE[fieldType] || OPS_BY_TYPE.text;
  const isPlaylist = fieldType === 'playlist';

  const handleFieldChange = (field) => {
    const newType = getAdditiveFieldType(field);
    const newOps = OPS_BY_TYPE[newType];
    onChange({ ...condition, field, op: newOps ? newOps[0].value : 'playlist_list', value: '' });
  };

  return (
    <div style={styles.conditionRow}>
      <select style={styles.select} value={condition.field} onChange={e => handleFieldChange(e.target.value)}>
        {ADDITIVE_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>
      {!isPlaylist && (
        <select style={styles.select} value={condition.op} onChange={e => onChange({ ...condition, op: e.target.value, value: '' })}>
          {ops.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
        </select>
      )}
      {isPlaylist ? (
        <PlaylistPicker value={condition.value} onChange={val => onChange({ ...condition, value: val })} />
      ) : (
        <input
          style={styles.input}
          type="text"
          value={condition.value}
          placeholder="value…"
          onChange={e => onChange({ ...condition, value: e.target.value })}
        />
      )}
      <button style={styles.removeBtn} onClick={onRemove} title="Remove">✕</button>
    </div>
  );
}

export default function SmartPlaylistBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEditing = Boolean(id);

  const today = new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const [name, setName] = useState(isEditing ? '' : `${today} Playlist`);
  const [conditions, setConditions] = useState([newCondition()]);
  const [additiveConditions, setAdditiveConditions] = useState([]);
  const [limit, setLimit] = useState('');
  const [orderBy, setOrderBy] = useState('-date_added');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { playQueue, currentTrack } = usePlayerStore();

  const { data: existingPlaylist } = useQuery(
    ['playlist', id],
    () => getPlaylist(id).then(r => r.data),
    { enabled: isEditing }
  );

  useEffect(() => {
    if (existingPlaylist?.smart_rules) {
      setName(existingPlaylist.name);
      const rules = existingPlaylist.smart_rules;
      const allConditions = (rules.conditions || []).map(c => ({ ...c, id: Date.now() + Math.random() }));
      setConditions(allConditions.filter(c => !c.additive && c.op !== 'playlist_list').length
        ? allConditions.filter(c => !c.additive && c.op !== 'playlist_list')
        : [newCondition()]);
      setAdditiveConditions(allConditions.filter(c => c.additive || c.op === 'playlist_list'));
      setLimit(rules.limit || '');
      setOrderBy(rules.order_by || '-date_added');
    }
  }, [existingPlaylist]);

  const buildRules = () => ({
    match: 'all',
    conditions: [
      ...conditions.map(({ id, ...c }) => c),
      ...additiveConditions.map(({ id, ...c }) => ({ ...c, additive: true })),
    ],
    limit: limit ? parseInt(limit) : null,
    order_by: orderBy,
  });

  const save = useMutation(
    () => {
      const payload = { name, is_smart: true, smart_rules: buildRules() };
      return isEditing ? updatePlaylist(id, payload) : createPlaylist(payload);
    },
    {
      onSuccess: (res) => {
        qc.invalidateQueries('playlists');
        navigate(`/playlists/${res.data.id}`);
      }
    }
  );

  const handlePreview = async () => {
    if (!name) return;
    setPreviewLoading(true);
    try {
      const payload = { name, is_smart: true, smart_rules: buildRules() };
      let playlistId = id;
      if (!isEditing) {
        const res = await createPlaylist(payload);
        playlistId = res.data.id;
        navigate(`/playlists/${playlistId}/edit`, { replace: true });
      } else {
        await updatePlaylist(id, payload);
      }
      const tracks = await getPlaylistTracks(playlistId).then(r => r.data);
      setPreview(Array.isArray(tracks) ? tracks : tracks?.results || []);
      qc.invalidateQueries('playlists');
    } catch (e) {
      console.error(e);
    }
    setPreviewLoading(false);
  };

  const updateCondition = (idx, updated) => setConditions(prev => prev.map((c, i) => i === idx ? updated : c));
  const removeCondition = (idx) => setConditions(prev => prev.filter((_, i) => i !== idx));
  const updateAdditiveCondition = (idx, updated) => setAdditiveConditions(prev => prev.map((c, i) => i === idx ? updated : c));
  const removeAdditiveCondition = (idx) => setAdditiveConditions(prev => prev.filter((_, i) => i !== idx));

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>{isEditing ? 'Edit Smart Playlist' : 'New Smart Playlist'}</h1>
        <p style={styles.subtitle}>Define rules to automatically populate this playlist</p>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Playlist Name</label>
        <input
          style={{ ...styles.input, width: '100%', maxWidth: 400 }}
          placeholder="e.g. 90s Hip-Hop Favourites"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>

      {/* Filter conditions — always AND */}
      <div style={styles.section}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Match ALL of these conditions:</span>
        </div>
        {conditions.map((cond, idx) => (
          <ConditionRow
            key={cond.id}
            condition={cond}
            onChange={updated => updateCondition(idx, updated)}
            onRemove={() => removeCondition(idx)}
            canRemove={conditions.length > 1}
          />
        ))}
        <button style={styles.addBtn} onClick={() => setConditions(prev => [...prev, newCondition()])}>
          + Add Condition
        </button>
      </div>

      {/* Additive conditions — always OR */}
      <div style={{ ...styles.section, background: 'var(--bg2)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>➕ Also include tracks matching ANY of these:</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 10 }}>
            Artists, songs, albums, or playlists always added regardless of conditions above
          </span>
        </div>
        {additiveConditions.map((cond, idx) => (
          <AdditiveConditionRow
            key={cond.id}
            condition={cond}
            onChange={updated => updateAdditiveCondition(idx, updated)}
            onRemove={() => removeAdditiveCondition(idx)}
          />
        ))}
        <button style={styles.addBtn} onClick={() => setAdditiveConditions(prev => [...prev, newAdditiveCondition()])}>
          + Add
        </button>
      </div>

      <div style={{ ...styles.section, marginTop: 24 }}>
        <div style={styles.limitRow}>
          <label style={styles.label}>Limit to</label>
          <input style={{ ...styles.input, width: 80 }} type="number" placeholder="∞" value={limit} onChange={e => setLimit(e.target.value)} />
          <label style={styles.label}>tracks, ordered by</label>
          <select style={styles.select} value={orderBy} onChange={e => setOrderBy(e.target.value)}>
            {ORDER_BY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div style={styles.actions}>
        <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => navigate('/playlists')}>Cancel</button>
        <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={handlePreview} disabled={!name || previewLoading}>
          {previewLoading ? 'Loading…' : '👁 Preview'}
        </button>
        <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => save.mutate()} disabled={!name || save.isLoading}>
          {save.isLoading ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Playlist'}
        </button>
      </div>

      {preview && (
        <div style={styles.preview}>
          <div style={styles.previewHeader}>
            <span style={styles.previewTitle}>Preview — {preview.length} tracks</span>
            {preview.length > 0 && (
              <button style={{ ...styles.btn, ...styles.btnPrimary, padding: '6px 14px', fontSize: 12 }}
                onClick={() => playQueue(preview, 0)}>▶ Play All</button>
            )}
          </div>
          {preview.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', padding: '20px 0' }}>No tracks match these rules.</p>
          ) : (
            <table className="track-table">
              <thead>
                <tr><th>#</th><th>Title</th><th>Artist</th><th>Album</th><th>Genre</th><th>Year</th><th>Plays</th></tr>
              </thead>
              <tbody>
                {preview.slice(0, 50).map((track, i) => (
                  <tr key={track.id} className={currentTrack?.id === track.id ? 'playing' : ''} onDoubleClick={() => playQueue(preview, i)}>
                    <td style={{ color: 'var(--text-muted)', textAlign: 'center' }}>{currentTrack?.id === track.id ? '♪' : i + 1}</td>
                    <td><strong>{track.title}</strong></td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.artist_name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.album_title}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.genre_name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.year || '—'}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.play_count}</td>
                  </tr>
                ))}
                {preview.length > 50 && (
                  <tr><td colSpan={7} style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 12 }}>
                    …and {preview.length - 50} more tracks
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 900, margin: '0 auto' },
  header: { marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 700 },
  subtitle: { color: 'var(--text-muted)', marginTop: 4, fontSize: 13 },
  section: { marginBottom: 24 },
  label: { fontSize: 13, color: 'var(--text-muted)', marginRight: 8 },
  conditionRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  select: { padding: '7px 10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, outline: 'none', cursor: 'pointer' },
  input: { padding: '7px 10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, outline: 'none', minWidth: 160 },
  removeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: '4px 6px', borderRadius: 4 },
  addBtn: { background: 'none', border: '1px dashed var(--border)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: '6px 14px', borderRadius: 6, marginTop: 4 },
  limitRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  actions: { display: 'flex', gap: 10, marginBottom: 32 },
  btn: { padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  btnPrimary: { background: 'var(--accent)', color: '#fff' },
  btnSecondary: { background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)' },
  preview: { background: 'var(--bg2)', borderRadius: 10, padding: 20, border: '1px solid var(--border)' },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  previewTitle: { fontWeight: 600, fontSize: 14 },
};